import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import {
  LandlordDetailedReportRowModel,
  LandlordDetailedReportSearchModel,
  LandlordDetailedReportTotalsModel,
  LandlordReportFilterOptionsModel
} from '../shared/models/landlord-detailed-report.model';
import { trackPage } from '../shared/services/utils/tracking/tracking.service';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  getLandlordDetailedReport,
  getLandlordReportFilterOptions
} from './landlord-detailed-report.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_TOTALS: LandlordDetailedReportTotalsModel = {
  totalRentGeneratedPerUnit: 0,
  amountDueToLandlord: 0
};

const EMPTY_OPTIONS: LandlordReportFilterOptionsModel = {
  landlords: [],
  areas: [],
  properties: [],
  unitTypes: [],
  unitTypeGroups: [],
  rooms: []
};

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

type StringOption = { value: string; label: string };
type NumberOption = { value: number; label: string };

const LandlordDetailedReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<LandlordDetailedReportRowModel[]>([]);
  const [listCount, setListCount] = useState(0);
  const [totals, setTotals] = useState<LandlordDetailedReportTotalsModel>(EMPTY_TOTALS);
  const [truncated, setTruncated] = useState(false);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [checkInDate, setCheckInDate] = useState<Date>(moment().startOf('month').toDate());
  // The window's end date is an inclusive night, so a whole month is the 1st to the 31st.
  const [checkOutDate, setCheckOutDate] = useState<Date>(moment().endOf('month').toDate());

  const [landlordId, setLandlordId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [unitTypeId, setUnitTypeId] = useState<number | undefined>(undefined);
  const [unitTypeGroupId, setUnitTypeGroupId] = useState('');
  const [roomId, setRoomId] = useState('');

  const [options, setOptions] = useState<LandlordReportFilterOptionsModel>(EMPTY_OPTIONS);

  useEffect(() => {
    trackPage('landlord-detailed-report');

    getLandlordReportFilterOptions().then((response) => {
      if (!isErrorInfo(response)) {
        setOptions(response);
      } else {
        birdToast.error('Filters could not be loaded', 'Landlord detailed report');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The dropdowns cascade in the order they are displayed: each one narrows every dropdown after
  // it. Landlord is last and therefore the most constrained — by the time you reach it, it only
  // offers landlords who actually own something in the current selection.
  //
  // Each stage is the set of unit types still reachable, and every dropdown is derived from the
  // stage above it. Deriving from unit types (rather than from the flat lists) is what lets a
  // later filter constrain an earlier dimension.
  const byArea = useMemo(
    () => options.unitTypes.filter((unitType) => !areaId || unitType.areaId === areaId),
    [areaId, options.unitTypes]
  );

  const byProperty = useMemo(
    () => byArea.filter((unitType) => !propertyId || unitType.propertyId === propertyId),
    [byArea, propertyId]
  );

  const byUnit = useMemo(
    () => byProperty.filter((unitType) => !unitTypeId || unitType.id === unitTypeId),
    [byProperty, unitTypeId]
  );

  const byGroup = useMemo(
    () =>
      byUnit.filter((unitType) => !unitTypeGroupId || unitType.unitTypeGroupId === unitTypeGroupId),
    [byUnit, unitTypeGroupId]
  );

  const byRoom = useMemo(() => {
    if (!roomId) return byGroup;
    const owner = options.rooms.find((room) => room.roomId === roomId);
    return owner ? byGroup.filter((unitType) => unitType.id === owner.unitTypeId) : byGroup;
  }, [byGroup, options.rooms, roomId]);

  // Name lookups, so each dropdown can label an id without carrying the name around.
  const areaNames = useMemo(
    () => new Map(options.areas.map((area) => [area.id, area.name])),
    [options.areas]
  );
  const propertyNames = useMemo(
    () => new Map(options.properties.map((property) => [property.id, property.name])),
    [options.properties]
  );
  const groupNames = useMemo(
    () => new Map(options.unitTypeGroups.map((group) => [group.id, group.name])),
    [options.unitTypeGroups]
  );
  const landlordNames = useMemo(
    () => new Map(options.landlords.map((landlord) => [landlord.id, landlord.name])),
    [options.landlords]
  );

  const distinct = (
    unitTypes: LandlordReportFilterOptionsModel['unitTypes'],
    key: 'areaId' | 'propertyId' | 'unitTypeGroupId' | 'landlordId',
    names: Map<string, string>
  ): StringOption[] => {
    const seen = new Map<string, string>();
    unitTypes.forEach((unitType) => {
      const id = unitType[key];
      if (id) seen.set(id, names.get(id) || id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  };

  const areaOptions: StringOption[] = useMemo(
    () => distinct(options.unitTypes, 'areaId', areaNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areaNames, options.unitTypes]
  );

  const propertyOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byArea, 'propertyId', propertyNames),
    [byArea, propertyNames]
  );

  const unitTypeOptions: NumberOption[] = useMemo(() => {
    const seen = new Map<number, string>();
    byProperty.forEach((unitType) => seen.set(unitType.id, unitType.name || String(unitType.id)));
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  }, [byProperty]);

  const unitTypeGroupOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byUnit, 'unitTypeGroupId', groupNames),
    [byUnit, groupNames]
  );

  const roomOptions: StringOption[] = useMemo(() => {
    const reachable = new Set(byGroup.map((unitType) => unitType.id));
    return options.rooms
      .filter((room) => reachable.has(room.unitTypeId))
      .map((room) => ({ value: room.roomId, label: room.shortName }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [byGroup, options.rooms]);

  // Last in the chain, so it sees every other selection.
  const landlordOptions: StringOption[] = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => distinct(byRoom, 'landlordId', landlordNames),
    [byRoom, landlordNames]
  );

  // Narrowing an earlier filter can strand a later selection that is no longer offered. Clearing
  // it here rather than in each onChange means the rule holds however the selection was reached,
  // including the filter-options response arriving after a choice was made.
  useEffect(() => {
    if (propertyId && !propertyOptions.some((option) => option.value === propertyId)) {
      setPropertyId('');
    }
  }, [propertyId, propertyOptions]);

  useEffect(() => {
    if (unitTypeId && !unitTypeOptions.some((option) => option.value === unitTypeId)) {
      setUnitTypeId(undefined);
    }
  }, [unitTypeId, unitTypeOptions]);

  useEffect(() => {
    if (
      unitTypeGroupId &&
      !unitTypeGroupOptions.some((option) => option.value === unitTypeGroupId)
    ) {
      setUnitTypeGroupId('');
    }
  }, [unitTypeGroupId, unitTypeGroupOptions]);

  useEffect(() => {
    if (roomId && !roomOptions.some((option) => option.value === roomId)) {
      setRoomId('');
    }
  }, [roomId, roomOptions]);

  useEffect(() => {
    if (landlordId && !landlordOptions.some((option) => option.value === landlordId)) {
      setLandlordId('');
    }
  }, [landlordId, landlordOptions]);

  const loadReport = useCallback(
    (page: number, perPage: number) => {
      setLoading(true);
      setError(false);

      const search: LandlordDetailedReportSearchModel = {
        checkInDate: moment(checkInDate).startOf('day').valueOf(),
        checkOutDate: moment(checkOutDate).startOf('day').valueOf(),
        offset: page,
        limit: perPage,
        query: {
          ...(landlordId ? { landlordId } : {}),
          ...(areaId ? { areaId } : {}),
          ...(propertyId ? { propertyId } : {}),
          ...(unitTypeId ? { unitTypeId } : {}),
          ...(unitTypeGroupId ? { unitTypeGroupId } : {}),
          ...(roomId ? { roomId } : {})
        }
      };

      getLandlordDetailedReport(search).then((response) => {
        setLoading(false);
        setHasSearched(true);

        if (isErrorInfo(response)) {
          setError(true);
          setRows([]);
          setListCount(0);
          setTotals(EMPTY_TOTALS);
          setTruncated(false);
          return;
        }

        setRows(response.rows);
        setListCount(response.count);
        setTotals(response.totals);
        setTruncated(response.truncated);
      });
    },
    [areaId, checkInDate, checkOutDate, landlordId, propertyId, roomId, unitTypeGroupId, unitTypeId]
  );

  const handleGenerate = () => {
    if (!checkInDate || !checkOutDate) {
      birdToast.error('Pick both a check-in and a check-out date', 'Landlord detailed report');
      return;
    }

    // Both window dates are inclusive, so an equal pair is a legitimate single-night window.
    if (moment(checkOutDate).isBefore(moment(checkInDate), 'day')) {
      birdToast.error('Check-out cannot be before check-in', 'Landlord detailed report');
      return;
    }

    setOffset(1);
    loadReport(1, limit);
  };

  const handleClearFilters = () => {
    setLandlordId('');
    setAreaId('');
    setPropertyId('');
    setUnitTypeId(undefined);
    setUnitTypeGroupId('');
    setRoomId('');
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadReport(newPage, limit);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadReport(1, itemsPerPage);
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="sticky top-0 z-30 -mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Landlord Detailed Report
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            Rent generated and amount due per landlord unit, pro-rated across the selected window.
            Both window dates are <span className="font-gilroy-bold">inclusive</span>, so a full
            month runs 1st to 31st. A guest is never charged for their own check-out day.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Area</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={areaOptions}
              placeholder="All areas"
              value={areaOptions.find((option) => option.value === areaId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => {
                setAreaId(value?.value || '');
                setPropertyId('');
                setUnitTypeId(undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Property
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={propertyOptions}
              placeholder="All properties"
              value={propertyOptions.find((option) => option.value === propertyId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => {
                setPropertyId(value?.value || '');
                setUnitTypeId(undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Unit</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={unitTypeOptions}
              placeholder="All units"
              value={unitTypeOptions.find((option) => option.value === unitTypeId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<NumberOption, false>) => {
                setUnitTypeId(value?.value ?? undefined);
                setRoomId('');
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Unit Type Group
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={unitTypeGroupOptions}
              placeholder="All unit type groups"
              value={
                unitTypeGroupOptions.find((option) => option.value === unitTypeGroupId) ?? null
              }
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) =>
                setUnitTypeGroupId(value?.value || '')
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Unit Short Name
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={roomOptions}
              placeholder="All short names"
              value={roomOptions.find((option) => option.value === roomId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) => setRoomId(value?.value || '')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Landlord
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={landlordOptions}
              placeholder={
                landlordOptions.length ? 'All landlords' : 'No landlords match the filters'
              }
              value={landlordOptions.find((option) => option.value === landlordId) ?? null}
              isClearable
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, false>) =>
                setLandlordId(value?.value || '')
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in Date
            </label>
            <ReactDatePicker
              selected={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckInDate(date);
                if (checkOutDate.getTime() < date.getTime()) setCheckOutDate(date);
              }}
              placeholderText="From"
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out Date
            </label>
            <ReactDatePicker
              selected={checkOutDate}
              minDate={checkInDate}
              onChange={(date: Date | null) => {
                if (!date) return;
                setCheckOutDate(date);
              }}
              placeholderText="To"
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleGenerate}
            type={ButtonType.primary}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Generate
          </Button>
          <Button
            onClick={handleClearFilters}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Clear filters
          </Button>
        </div>
      </section>

      <section className="mt-6 space-y-6">
        {truncated && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-lg font-gilroy-medium text-amber-700">
              This window returned more reservations than the report can total accurately. Narrow
              the date range or add a filter — the figures below are incomplete.
            </p>
          </div>
        )}

        {loading ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <LoadingData />
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
            <p className="text-base font-gilroy-bold uppercase tracking-[0.22em] text-rose-500">
              Finance
            </p>
            <h2 className="mt-3 text-4xl font-recoleta text-neutral-500">
              Unable to load the landlord detailed report
            </h2>
            <p className="mt-3 max-w-xl text-lg font-gilroy-medium text-neutral-400">
              The report could not be generated. Retry, or narrow the date range if the window is
              very wide.
            </p>
            <Button
              onClick={() => loadReport(offset, limit)}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        ) : !hasSearched ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <h2 className="text-3xl font-recoleta text-neutral-500">
              Choose a window and hit Generate
            </h2>
            <p className="mt-3 text-lg font-gilroy-medium text-neutral-400">
              Rent is pro-rated across the dates you pick, so the report always needs a check-in and
              a check-out date.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <NotFoundData />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[2500px] w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="border-b border-gray-200 text-neutral-900 [&>th]:py-4 [&>th]:font-gilroy-bold [&>th]:text-lg [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-[0.12em]">
                      <th className="px-4 text-left whitespace-nowrap">Reservation ID</th>
                      <th className="px-4 text-left whitespace-nowrap">Landlord ID</th>
                      <th className="px-4 text-left whitespace-nowrap">Landlord Name</th>
                      <th className="px-4 text-left whitespace-nowrap">Landlord Phone</th>
                      <th className="px-4 text-left whitespace-nowrap">Landlord Email</th>
                      <th className="px-4 text-left whitespace-nowrap">Property Name</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit ID</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit Name</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit Short Name</th>
                      <th className="px-4 text-left whitespace-nowrap">Unit Type Group</th>
                      <th className="px-4 text-left whitespace-nowrap">Area</th>
                      <th className="px-4 text-left whitespace-nowrap">Area Name</th>
                      <th className="px-4 text-right whitespace-nowrap">Bedrooms</th>
                      <th className="px-4 text-right whitespace-nowrap">Total Rent Generated</th>
                      <th className="px-4 text-right whitespace-nowrap">Amount Due to Landlord</th>
                      <th className="px-4 text-left whitespace-nowrap">Check-in Date</th>
                      <th className="px-4 text-left whitespace-nowrap">Check-out Date</th>
                      <th className="px-4 text-right whitespace-nowrap">Fee Per Day</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.reservationId}
                        className="transition-colors hover:bg-creams-50 [&>td]:border-t [&>td]:border-gray-100 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl [&>td]:text-neutral-500"
                      >
                        <td className="font-gilroy-bold">{row.reservationId}</td>
                        <td className="font-gilroy-regular">{row.landlordId || '—'}</td>
                        <td>{row.landlordName || '—'}</td>
                        <td className="font-gilroy-regular">{row.landlordPhone || '—'}</td>
                        <td className="font-gilroy-regular">{row.landlordEmail || '—'}</td>
                        <td>{row.propertyName || '—'}</td>
                        <td>{row.unitId}</td>
                        <td>{row.unitName || '—'}</td>
                        <td>{row.unitShortName || '—'}</td>
                        <td>{row.unitTypeGroupName || '—'}</td>
                        <td className="font-gilroy-regular">{row.areaId || '—'}</td>
                        <td>{row.areaName || '—'}</td>
                        <td className="text-right">{row.bedrooms ?? '—'}</td>
                        <td className="text-right font-gilroy-bold">
                          {formatMoney(row.totalRentGeneratedPerUnit)}
                        </td>
                        <td className="text-right font-gilroy-bold">
                          {formatMoney(row.amountDueToLandlord)}
                        </td>
                        <td>{formatDate(row.checkInDate)}</td>
                        <td>{formatDate(row.checkOutDate)}</td>
                        <td className="text-right">{formatMoney(row.feePerDay)}</td>
                      </tr>
                    ))}
                  </tbody>

                  {/* Totals span the whole filtered result, not this page — so paging never changes them. */}
                  <tfoot className="sticky bottom-0 bg-white">
                    <tr className="[&>td]:border-t-2 [&>td]:border-gray-300 [&>td]:px-4 [&>td]:py-4 [&>td]:text-xl [&>td]:font-gilroy-bold [&>td]:text-neutral-900">
                      <td colSpan={13} className="text-left uppercase tracking-[0.12em]">
                        Totals — all {listCount} reservations
                      </td>
                      <td className="text-right">
                        {formatMoney(totals.totalRentGeneratedPerUnit)}
                      </td>
                      <td className="text-right">{formatMoney(totals.amountDueToLandlord)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {listCount > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={listCount}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default LandlordDetailedReportPage;
